import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import ImageDropZone from '../../components/common/ImageDropZone';
import * as adminMediaService from '../../services/adminMedia.service';

/**
 * General-purpose image library for Team365 — Jenn, 2026-08-28: "Give
 * Team365 ability to upload images for use in 365reset.ca and on social
 * media channels." Upload once, copy the URL, paste it wherever it's
 * needed (e.g. Content Pushes' "Image URL" field already accepts any
 * URL, not just Google Drive links). Separate from the affiliate-scoped
 * media library in the Affiliate Control Room.
 */
export default function AdminMediaLibrary({ embedded }) {
  const [media, setMedia] = useState(null);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try { setMedia(await adminMediaService.listMedia()); } catch (err) { setError(err.response?.data?.error || 'Could not load the media library.'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUploaded = async (url) => {
    try {
      await adminMediaService.createMedia({ url });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this image.');
    }
  };

  const handleCopy = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard permission denied — nothing to do */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this image from the library?')) return;
    try {
      await adminMediaService.deleteMedia(id);
      setMedia(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove this image.');
    }
  };

  const body = (
    <>
      {!embedded && (
        <div className="mb-6">
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Media Library</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Upload an image once, copy its link, and paste it anywhere a URL is needed — the marketing site, a social post, or Content Pushes' Image URL field.
          </p>
        </div>
      )}

      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

      <div className="bg-white rounded-2xl shadow-sm p-4 mb-5 max-w-sm">
        <ImageDropZone currentUrl={null} onUploaded={handleUploaded} onClear={() => {}} />
      </div>

      {!media ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : media.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
          No images uploaded yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {media.map(item => (
            <div key={item.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <img src={item.url} alt="" className="w-full h-32 object-cover bg-gray-100" />
              <div className="p-2 space-y-1">
                <p className="text-[10px] text-gray-400">
                  {new Date(item.created_at).toLocaleDateString()}{item.uploaded_by_name ? ` · ${item.uploaded_by_name}` : ''}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => handleCopy(item)} className="flex-1 text-xs font-medium text-reset-terracotta hover:underline text-left">
                    {copiedId === item.id ? 'Copied!' : 'Copy URL'}
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="text-xs text-gray-300 hover:text-red-500">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />
        {body}
      </div>
    </DashboardLayout>
  );
}
